// src/services/categories.service.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";
import { db } from "./firebase/firebaseconfig";

// Fixed categories that cannot be deleted - YOUR 6 NEW CATEGORIES
const FIXED_CATEGORIES = [
  {
    id: 'fixed-1',
    name: 'Bureau Directionnel',
    description: 'Bureaux directionnels professionnels et ergonomiques',
    color: '#4ECDC4',
    icon: '🖥️',
    isFixed: true,
    order: 1
  },
  {
    id: 'fixed-2',
    name: 'Mobilier Métallique',
    description: 'Mobilier métallique robuste et durable',
    color: '#95A5A6',
    icon: '🔩',
    isFixed: true,
    order: 2
  },
  {
    id: 'fixed-3',
    name: 'Tables',
    description: 'Tables de réunion et tables basses',
    color: '#FFD93D',
    icon: '🗂️',
    isFixed: true,
    order: 3
  },
  {
    id: 'fixed-4',
    name: 'Comptoir ',
    description: 'Comptoirs d\'accueil et de réception',
    color: '#E74C3C',
    icon: '🏢',
    isFixed: true,
    order: 4
  },
  {
    id: 'fixed-5',
    name: 'Coffre Fort',
    description: 'Coffres-forts sécurisés pour vos biens',
    color: '#34495E',
    icon: '🔒',
    isFixed: true,
    order: 5
  },
  {
    id: 'fixed-6',
    name: 'Chaise',
    description: 'Chaises de bureau confortables et design',
    color: '#FF6B6B',
    icon: '🪑',
    isFixed: true,
    order: 6
  },
];

class CategoriesService {
  constructor() {
    this.collectionName = "categories";
    this.productsCollectionName = "products";
  }

  /**
   * Initialize/Update fixed categories in Firestore
   * This ensures the 6 fixed categories always exist with correct data
   */
  async initializeFixedCategories() {
    try {
      const batch = writeBatch(db);
      
      for (const category of FIXED_CATEGORIES) {
        const categoryRef = doc(db, this.collectionName, category.id);
        batch.set(categoryRef, {
          ...category,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true }); // merge: true keeps existing timestamps if they exist
      }

      await batch.commit();
      console.log('Fixed categories initialized successfully');
      
      return {
        success: true,
        message: 'Fixed categories initialized'
      };
    } catch (error) {
      console.error('Error initializing fixed categories:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all categories (fixed + dynamic) with product counts
   */
  async getAllCategories() {
    try {
      // First, ensure fixed categories exist
      await this.initializeFixedCategories();

      const categoriesRef = collection(db, this.collectionName);
      const q = query(categoriesRef, orderBy("order", "asc"));
      const querySnapshot = await getDocs(q);

      const categories = [];
      querySnapshot.forEach((doc) => {
        categories.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort: fixed categories first (by order), then dynamic (by createdAt)
      categories.sort((a, b) => {
        if (a.isFixed && !b.isFixed) return -1;
        if (!a.isFixed && b.isFixed) return 1;
        if (a.isFixed && b.isFixed) return (a.order || 0) - (b.order || 0);
        return 0;
      });

      // Get product counts for each category
      const productsRef = collection(db, this.productsCollectionName);
      const productsSnapshot = await getDocs(productsRef);
      
      const categoriesWithCounts = categories.map(category => {
        let productCount = 0;
        productsSnapshot.forEach((doc) => {
          if (doc.data().category === category.name) {
            productCount++;
          }
        });
        return {
          ...category,
          productCount
        };
      });

      return {
        success: true,
        categories: categoriesWithCounts
      };
    } catch (error) {
      console.error('Error getting categories:', error);
      return {
        success: false,
        error: error.message,
        categories: []
      };
    }
  }

  /**
   * Get fixed categories only
   */
  async getFixedCategories() {
    try {
      const result = await this.getAllCategories();
      const fixedCategories = result.categories.filter(cat => cat.isFixed);
      return {
        success: true,
        categories: fixedCategories
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        categories: []
      };
    }
  }

  /**
   * Get dynamic categories only (for "Autres" dropdown)
   */
  async getDynamicCategories() {
    try {
      const result = await this.getAllCategories();
      const dynamicCategories = result.categories.filter(cat => !cat.isFixed);
      console.log('Dynamic Categories:', dynamicCategories);
      return {
        success: true,
        categories: dynamicCategories
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        categories: []
      };
    }
  }

  /**
   * Get category by ID
   */
  async getCategoryById(id) {
    try {
      const categoryRef = doc(db, this.collectionName, id);
      const categorySnap = await getDoc(categoryRef);

      if (!categorySnap.exists()) {
        return {
          success: false,
          error: 'Category not found',
          category: null
        };
      }

      return {
        success: true,
        category: {
          id: categorySnap.id,
          ...categorySnap.data()
        }
      };
    } catch (error) {
      console.error('Error getting category:', error);
      return {
        success: false,
        error: error.message,
        category: null
      };
    }
  }

  /**
   * Add new dynamic category
   */
  async addCategory(categoryData) {
    try {
      // Check if category with same name already exists
      const categoriesRef = collection(db, this.collectionName);
      const q = query(categoriesRef, where("name", "==", categoryData.name));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return {
          success: false,
          error: 'Une catégorie avec ce nom existe déjà'
        };
      }

      // Get the highest order number for dynamic categories
      const allCategoriesResult = await this.getAllCategories();
      const dynamicCategories = allCategoriesResult.categories.filter(cat => !cat.isFixed);
      const maxOrder = dynamicCategories.length > 0 
        ? Math.max(...dynamicCategories.map(cat => cat.order || 100)) 
        : 100;

      // Add new category
      const docRef = await addDoc(categoriesRef, {
        ...categoryData,
        isFixed: false,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        category: {
          id: docRef.id,
          ...categoryData,
          isFixed: false
        },
        message: 'Catégorie ajoutée avec succès'
      };
    } catch (error) {
      console.error('Error adding category:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update category (can update fixed category details, but not delete them)
   */
  async updateCategory(id, categoryData) {
    try {
      const categoryRef = doc(db, this.collectionName, id);
      const categorySnap = await getDoc(categoryRef);

      if (!categorySnap.exists()) {
        return {
          success: false,
          error: 'Category not found'
        };
      }

      const existingCategory = categorySnap.data();
      const oldCategoryName = existingCategory.name;

      // Check if another category with same name exists
      const categoriesRef = collection(db, this.collectionName);
      const q = query(categoriesRef, where("name", "==", categoryData.name));
      const querySnapshot = await getDocs(q);

      let nameExists = false;
      querySnapshot.forEach((doc) => {
        if (doc.id !== id) {
          nameExists = true;
        }
      });

      if (nameExists) {
        return {
          success: false,
          error: 'Une catégorie avec ce nom existe déjà'
        };
      }

      // Update category (preserve isFixed and order)
      await updateDoc(categoryRef, {
        ...categoryData,
        isFixed: existingCategory.isFixed,
        order: existingCategory.order,
        updatedAt: serverTimestamp()
      });

      // If name changed, update all products using this category
      if (oldCategoryName !== categoryData.name) {
        const productsRef = collection(db, this.productsCollectionName);
        const productsQuery = query(productsRef, where("category", "==", oldCategoryName));
        const productsSnapshot = await getDocs(productsQuery);

        const batch = writeBatch(db);
        productsSnapshot.forEach((doc) => {
          batch.update(doc.ref, { category: categoryData.name });
        });
        
        if (!productsSnapshot.empty) {
          await batch.commit();
        }
      }

      return {
        success: true,
        category: {
          id,
          ...categoryData,
          isFixed: existingCategory.isFixed
        },
        message: 'Catégorie modifiée avec succès'
      };
    } catch (error) {
      console.error('Error updating category:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete category (only dynamic categories can be deleted)
   */
  async deleteCategory(id) {
    try {
      const categoryRef = doc(db, this.collectionName, id);
      const categorySnap = await getDoc(categoryRef);

      if (!categorySnap.exists()) {
        return {
          success: false,
          error: 'Category not found'
        };
      }

      const category = categorySnap.data();

      // Prevent deletion of fixed categories
      if (category.isFixed) {
        return {
          success: false,
          error: 'Les catégories principales ne peuvent pas être supprimées'
        };
      }

      // Check if any products use this category
      const productsRef = collection(db, this.productsCollectionName);
      const productsQuery = query(productsRef, where("category", "==", category.name));
      const productsSnapshot = await getDocs(productsQuery);

      if (!productsSnapshot.empty) {
        return {
          success: false,
          error: `Impossible de supprimer: ${productsSnapshot.size} produit(s) utilisent cette catégorie`
        };
      }

      // Delete category
      await deleteDoc(categoryRef);

      return {
        success: true,
        message: 'Catégorie supprimée avec succès'
      };
    } catch (error) {
      console.error('Error deleting category:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

const categoriesService = new CategoriesService();
export default categoriesService;